import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API, useAuth } from '../App';

function formatError(detail) {
  if (!detail) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) return detail.map(e => e?.msg || JSON.stringify(e)).join(' ');
  return String(detail);
}

export default function AuthModal({ onClose, onSuccess, initialTab = 'login' }) {
  const { login, register } = useAuth();
  const [tab, setTab] = useState(initialTab);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Login form
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');

  // Register form
  const [regName, setRegName] = useState('');
  const [regEmail, setRegEmail] = useState('');
  const [regPassword, setRegPassword] = useState('');

  // Forgot password form
  const [forgotEmail, setForgotEmail] = useState('');
  const [forgotSent, setForgotSent] = useState(false);

  // Lock background scroll while modal is open. Prevents iOS Safari from
  // scrolling the page behind the dim overlay when the user drags inside
  // the form, and stops the modal from feeling like it "goes behind" the
  // hero on small viewports.
  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prevOverflow; };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await login(loginEmail, loginPassword);
      onSuccess?.();
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    if (regPassword.length < 6) { setError('Password must be at least 6 characters'); return; }
    setLoading(true);
    try {
      await register(regName, regEmail, regPassword);
      onSuccess?.();
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleForgot = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await axios.post(`${API}/auth/forgot-password`, { email: forgotEmail });
      setForgotSent(true);
    } catch (err) {
      setError(formatError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-modal-overlay" onClick={onClose} data-testid="auth-modal-overlay">
      <div className="auth-modal-box" onClick={e => e.stopPropagation()} data-testid="auth-modal">
        <button className="auth-close" onClick={onClose} data-testid="auth-close-btn">×</button>

        <div style={{ marginBottom: 4 }}>
          <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 26, fontWeight: 300, color: 'var(--deep)' }}>
            <em>Little</em> Blue Numbers
          </div>
          <div style={{ fontSize: 13, color: 'var(--muted)', marginTop: 2 }}>
            {tab === 'login' ? 'Welcome back' : 'Save your reading'}
          </div>
        </div>

        <div className="auth-tabs">
          <button className={`auth-tab ${tab === 'login' ? 'active' : ''}`} onClick={() => { setTab('login'); setError(''); }} data-testid="login-tab">Sign in</button>
          <button className={`auth-tab ${tab === 'register' ? 'active' : ''}`} onClick={() => { setTab('register'); setError(''); }} data-testid="register-tab">Create account</button>
        </div>

        {error && <div className="auth-error" data-testid="auth-error">{error}</div>}

        {tab === 'login' && (
          <form onSubmit={handleLogin} data-testid="login-form">
            <label className="field-label">Email</label>
            <input
              className="field-input"
              type="email"
              value={loginEmail}
              onChange={e => setLoginEmail(e.target.value)}
              placeholder="your@email.com"
              required
              autoComplete="email"
              data-testid="login-email"
            />
            <label className="field-label">Password</label>
            <input
              className="field-input"
              type="password"
              value={loginPassword}
              onChange={e => setLoginPassword(e.target.value)}
              placeholder="Your password"
              required
              autoComplete="current-password"
              data-testid="login-password"
            />
            <button type="submit" className="auth-submit" disabled={loading} data-testid="login-submit">
              {loading ? 'Signing in...' : 'Sign in'}
            </button>
            <div style={{ textAlign: 'center', marginTop: 12 }}>
              <button
                type="button"
                onClick={() => { setTab('forgot'); setError(''); setForgotEmail(loginEmail); setForgotSent(false); }}
                style={{ background: 'none', border: 'none', fontSize: 12, color: 'var(--muted)', cursor: 'pointer', textDecoration: 'underline', padding: '4px 0', fontFamily: 'inherit' }}
                data-testid="forgot-password-link"
              >
                Forgot your password?
              </button>
            </div>
            <div className="auth-alt">
              Don't have an account?{' '}
              <button type="button" onClick={() => { setTab('register'); setError(''); }} data-testid="switch-to-register">Create one</button>
            </div>
          </form>
        )}

        {tab === 'forgot' && (
          <div data-testid="forgot-form">
            {!forgotSent ? (
              <form onSubmit={handleForgot}>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, fontWeight: 300, marginBottom: 6, marginTop: 8 }}>Reset your password</div>
                <p style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.6, marginBottom: 18 }}>
                  Enter the email you used to create your account and we'll send you a secure link to set a new password.
                </p>
                <label className="field-label">Email</label>
                <input
                  className="field-input"
                  type="email"
                  value={forgotEmail}
                  onChange={e => setForgotEmail(e.target.value)}
                  placeholder="your@email.com"
                  required
                  autoComplete="email"
                  data-testid="forgot-email"
                />
                <button type="submit" className="auth-submit" disabled={loading || !forgotEmail.trim()} data-testid="forgot-submit">
                  {loading ? 'Sending...' : 'Send reset link'}
                </button>
                <div className="auth-alt">
                  Remembered it?{' '}
                  <button type="button" onClick={() => { setTab('login'); setError(''); }} data-testid="switch-to-login-from-forgot">Back to sign in</button>
                </div>
              </form>
            ) : (
              <div style={{ textAlign: 'center', padding: '12px 0' }} data-testid="forgot-sent">
                <div style={{ fontSize: 32, marginBottom: 12 }}>✦</div>
                <div style={{ fontFamily: "'Cormorant Garamond', serif", fontSize: 22, marginBottom: 10 }}>Check your inbox</div>
                <p style={{ fontSize: 14, color: 'var(--muted)', lineHeight: 1.7, marginBottom: 20 }}>
                  If an account exists for <strong>{forgotEmail}</strong>, a password reset link is on its way. The link expires in 30 minutes.
                </p>
                <button
                  type="button"
                  onClick={() => { setTab('login'); setError(''); setForgotSent(false); }}
                  className="auth-submit"
                  data-testid="forgot-back-to-login"
                >
                  Back to sign in
                </button>
              </div>
            )}
          </div>
        )}

        {tab === 'register' && (
          <form onSubmit={handleRegister} data-testid="register-form">
            <label className="field-label">Your name</label>
            <input
              className="field-input"
              type="text"
              value={regName}
              onChange={e => setRegName(e.target.value)}
              placeholder="First name"
              required
              data-testid="register-name"
            />
            <label className="field-label">Email</label>
            <input
              className="field-input"
              type="email"
              value={regEmail}
              onChange={e => setRegEmail(e.target.value)}
              placeholder="your@email.com"
              required
              autoComplete="email"
              data-testid="register-email"
            />
            <label className="field-label">Password</label>
            <input
              className="field-input"
              type="password"
              value={regPassword}
              onChange={e => setRegPassword(e.target.value)}
              placeholder="At least 6 characters"
              required
              autoComplete="new-password"
              data-testid="register-password"
            />
            <button type="submit" className="auth-submit" disabled={loading} data-testid="register-submit">
              {loading ? 'Creating account...' : 'Create account'}
            </button>
            <div style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center', marginTop: 10, lineHeight: 1.6 }} data-testid="register-legal">
              By creating an account you agree to our{' '}
              <a href="/terms" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--muted)', textDecoration: 'underline' }}>Terms</a>
              {' '}and{' '}
              <a href="/privacy" target="_blank" rel="noopener noreferrer" style={{ color: 'var(--muted)', textDecoration: 'underline' }}>Privacy Policy</a>.
            </div>
            <div className="auth-alt">
              Already have an account?{' '}
              <button type="button" onClick={() => { setTab('login'); setError(''); }} data-testid="switch-to-login">Sign in</button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}
